import React, { useEffect, useMemo, useState } from 'react';

const SIZES = {
  xs: 20,
  sm: 28,
  md: 40,
  lg: 64
};

function getInitials(name) {
  const words = String(name || 'Club').trim().split(/\s+/).filter(Boolean);
  return words.slice(0, 2).map(word => word[0]).join('').toUpperCase() || 'CL';
}

function isManagedLogoUrl(value) {
  try {
    const url = new URL(String(value || ''));
    return url.protocol === 'https:' && url.hostname === 'res.cloudinary.com';
  } catch {
    return false;
  }
}

export default function ClubLogo({
  club,
  size = 'sm',
  decorative = true,
  label
}) {
  const pixels = typeof size === 'number' ? size : (SIZES[size] || SIZES.sm);
  const name = club?.name || club?.clubName || 'Club';
  const version = club?.logoUpdatedAt ? new Date(club.logoUpdatedAt).getTime() : '';
  const src = useMemo(() => {
    if (!isManagedLogoUrl(club?.logoUrl) && !String(club?.logoUrl || '').startsWith('blob:')) return '';
    const separator = club.logoUrl.includes('?') ? '&' : '?';
    return `${club.logoUrl}${version ? `${separator}v=${version}` : ''}`;
  }, [club?.logoUrl, version]);
  const [failed, setFailed] = useState(false);

  useEffect(() => setFailed(false), [src]);

  const sharedStyle = {
    width: pixels,
    height: pixels,
    minWidth: pixels,
    borderRadius: Math.max(6, Math.round(pixels * 0.22)),
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
    background: 'var(--bg-input)',
    border: '1px solid var(--border-color)',
    verticalAlign: 'middle'
  };

  if (src && !failed) {
    return (
      <span
        style={sharedStyle}
        role={decorative ? undefined : 'img'}
        aria-label={decorative ? undefined : (label || `${name} logo`)}
        aria-hidden={decorative ? 'true' : undefined}
      >
        <img
          src={src}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
      </span>
    );
  }

  return (
    <span
      style={{
        ...sharedStyle,
        color: 'var(--accent)',
        fontSize: Math.max(9, Math.round(pixels * 0.32)),
        fontWeight: 700,
        letterSpacing: '0.02em'
      }}
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : (label || `${name} logo placeholder`)}
      aria-hidden={decorative ? 'true' : undefined}
    >
      {getInitials(name)}
    </span>
  );
}
